import React, { useState } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Dropdown,
  Row,
  Col,
  Typography,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  MoreOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';

const { Title } = Typography;

interface DataTableProps<T> {
  title?: string;
  data: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  rowKey: string | ((record: T) => string);
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  filters?: React.ReactNode;
  extraActions?: React.ReactNode;
  selectionType?: 'checkbox' | 'radio';
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  selectedRowKeys?: React.Key[];
  expandable?: {
    expandedRowRender?: (record: T) => React.ReactNode;
    rowExpandable?: (record: T) => boolean;
  };

  onChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ) => void;
  bordered?: boolean;
  size?: 'small' | 'middle' | 'large';
  scroll?: { x?: number | string; y?: number | string };
  emptyText?: string;
  className?: string;
  rowClassName?: (record: T, index: number) => string;
  onRowClick?: (record: T) => void;
  actionColumn?: {
    width?: number;
    fixed?: 'left' | 'right';
    actions: (record: T) => Array<{
      key: string;
      label: React.ReactNode;
      icon?: React.ReactNode;
      danger?: boolean;
      disabled?: boolean;
      onClick: () => void;
    }>;
  };
}

export function DataTable<T extends Record<string, any>>({
  title,
  data,
  columns,
  loading = false,
  rowKey,
  pagination,
  searchable = true,
  searchPlaceholder = 'Search...',
  onSearch,
  onRefresh,
  onExport,
  filters,
  extraActions,
  selectionType,
  onSelectionChange,
  selectedRowKeys,
  expandable,

  onChange,
  bordered = false,
  size = 'middle',
  scroll,
  emptyText = 'No data available',
  className,
  rowClassName,
  onRowClick,
  actionColumn,
}: DataTableProps<T>) {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  };

  const getRowSelection = () => {
    if (!selectionType) return undefined;
    return {
      type: selectionType,
      selectedRowKeys,
      onChange: onSelectionChange,
    };
  };

  // Add action column if provided
  const finalColumns = actionColumn
    ? [
        ...columns,
        {
          title: 'Actions',
          key: 'actions',
          width: actionColumn.width || 80,
          fixed: actionColumn.fixed || 'right',
          render: (_: any, record: T) => {
            const actions = actionColumn.actions(record);
            return (
              <Dropdown
                menu={{
                  items: actions.map((action) => ({
                    key: action.key,
                    label: action.label,
                    icon: action.icon,
                    danger: action.danger,
                    disabled: action.disabled,
                    onClick: action.onClick,
                  })),
                }}
                placement="bottomRight"
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            );
          },
        },
      ]
    : columns;

  return (
    <Card className={className}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {title && <Title level={5} style={{ margin: 0 }}>{title}</Title>}
        </Col>
        <Col>
          <Space>
            {searchable && (
              <Input.Search
                placeholder={searchPlaceholder}
                allowClear
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
              />
            )}
            {filters && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
              >
                Filters
              </Button>
            )}
            {onRefresh && (
              <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                Refresh
              </Button>
            )}
            {onExport && (
              <Button icon={<ExportOutlined />} onClick={onExport}>
                Export
              </Button>
            )}
            {extraActions}
          </Space>
        </Col>
      </Row>

      {showFilters && filters && (
        <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
          {filters}
        </Card>
      )}

      <Table<T>
        dataSource={data}
        columns={finalColumns}
        loading={loading}
        rowKey={rowKey}
        rowSelection={getRowSelection()}
        expandable={expandable}
        onChange={onChange}
        bordered={bordered}
        size={size}
        scroll={scroll}
        pagination={
          pagination
            ? {
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: pagination.onChange,
              }
            : false
        }
        locale={{ emptyText }}
        onRow={
          onRowClick
            ? (record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
              })
            : undefined
        }
        rowClassName={rowClassName}
      />
    </Card>
  );
}

export default DataTable;
